import { useState, useEffect, useRef } from 'react';
import { useOutletContext } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Plus, Search, FileText, Edit, Trash2, Eye, X, Calendar, User,
  Save, Image, Globe, Share2, AlertCircle, CheckCircle2, Info, Upload, Loader2
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';

import { API_URL } from '../../config/api';

const statusOptions = [
  { value: 'draft', label: 'Brouillon', color: 'bg-slate-100 text-slate-600' },
  { value: 'published', label: 'Publié', color: 'bg-green-100 text-green-600' },
  { value: 'archived', label: 'Archivé', color: 'bg-amber-100 text-amber-600' },
];

const categoryOptions = [
  { value: 'recrutement', label: 'Recrutement' },
  { value: 'conseils', label: 'Conseils' },
  { value: 'actualites', label: 'Actualités' },
  { value: 'temoignages', label: 'Témoignages' },
];

// Image size recommendations
const IMAGE_SPECS = {
  cover: {
    width: 1200,
    height: 630,
    ratio: '1.91:1',
    description: 'Image de couverture (site)',
    tip: 'Format idéal pour l\'affichage sur le blog'
  },
  social: {
    width: 1200,
    height: 630,
    ratio: '1.91:1',
    description: 'Image de partage (réseaux sociaux)',
    tip: 'Format optimisé pour Facebook, LinkedIn, Twitter'
  },
  thumbnail: {
    width: 400,
    height: 300,
    ratio: '4:3',
    description: 'Miniature (liste articles)',
    tip: 'Affichée dans la liste des articles'
  }
};

export default function BlogManagePage() {
  const { user } = useOutletContext();
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingArticle, setEditingArticle] = useState(null);
  const [activeTab, setActiveTab] = useState('content'); // content, seo, images
  const [uploadingCover, setUploadingCover] = useState(false);
  const [uploadingOg, setUploadingOg] = useState(false);
  const coverInputRef = useRef(null);
  const ogInputRef = useRef(null);
  const [newArticle, setNewArticle] = useState({
    title: '',
    slug: '',
    excerpt: '',
    content: '',
    image_url: '',
    og_image_url: '',
    category: 'recrutement',
    status: 'draft',
    tags: [],
    // SEO fields
    meta_title: '',
    meta_description: '',
    meta_keywords: '',
    canonical_url: '',
    no_index: false
  });
  const [newTag, setNewTag] = useState('');
  const [seoScore, setSeoScore] = useState({ score: 0, tips: [] });

  useEffect(() => {
    fetchArticles();
  }, []);

  useEffect(() => {
    // Calculate SEO score when relevant fields change
    calculateSeoScore();
  }, [newArticle.title, newArticle.meta_title, newArticle.meta_description, newArticle.meta_keywords, newArticle.excerpt]);

  const calculateSeoScore = () => {
    const tips = [];
    let score = 0;
    const maxScore = 100;

    // Title checks (20 points)
    const title = newArticle.meta_title || newArticle.title;
    if (title) {
      if (title.length >= 30 && title.length <= 60) {
        score += 20;
      } else if (title.length > 0) {
        score += 10;
        if (title.length < 30) tips.push('Le titre SEO devrait faire au moins 30 caractères');
        if (title.length > 60) tips.push('Le titre SEO devrait faire moins de 60 caractères');
      }
    } else {
      tips.push('Ajoutez un titre SEO');
    }

    // Meta description checks (25 points)
    const metaDesc = newArticle.meta_description;
    if (metaDesc) {
      if (metaDesc.length >= 120 && metaDesc.length <= 160) {
        score += 25;
      } else if (metaDesc.length > 0) {
        score += 12;
        if (metaDesc.length < 120) tips.push('La méta description devrait faire au moins 120 caractères');
        if (metaDesc.length > 160) tips.push('La méta description devrait faire moins de 160 caractères');
      }
    } else {
      tips.push('Ajoutez une méta description');
    }

    // Keywords checks (15 points)
    if (newArticle.meta_keywords && newArticle.meta_keywords.length > 0) {
      score += 15;
    } else {
      tips.push('Ajoutez des mots-clés SEO');
    }

    // Excerpt checks (15 points)
    if (newArticle.excerpt && newArticle.excerpt.length >= 50) {
      score += 15;
    } else {
      tips.push('Ajoutez un extrait d\'au moins 50 caractères');
    }

    // Image checks (25 points)
    if (newArticle.image_url) {
      score += 15;
    } else {
      tips.push('Ajoutez une image de couverture');
    }
    if (newArticle.og_image_url) {
      score += 10;
    } else {
      tips.push('Ajoutez une image pour le partage social');
    }

    setSeoScore({ score: Math.min(score, maxScore), tips });
  };

  const fetchArticles = async () => {
    const token = localStorage.getItem('client_token');
    if (!token) return;

    try {
      const response = await fetch(`${API_URL}/api/blog`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setArticles(data);
      }
    } catch (error) {
      console.error('Error fetching articles:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateArticle = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem('client_token');

    const slug = newArticle.slug || newArticle.title
      .toLowerCase()
      .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '');

    try {
      const response = await fetch(`${API_URL}/api/blog`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...newArticle,
          slug,
          author: user.name
        })
      });

      if (response.ok) {
        toast.success('Article créé avec succès');
        setShowCreateModal(false);
        resetForm();
        fetchArticles();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Erreur lors de la création');
      }
    } catch (error) {
      toast.error('Erreur lors de la création');
    }
  };

  const handleUpdateArticle = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem('client_token');

    try {
      const response = await fetch(`${API_URL}/api/blog/${editingArticle.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(newArticle)
      });

      if (response.ok) {
        toast.success('Article mis à jour');
        setEditingArticle(null);
        setShowCreateModal(false);
        resetForm();
        fetchArticles();
      } else {
        toast.error('Erreur lors de la mise à jour');
      }
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const handleDeleteArticle = async (articleId) => {
    if (!confirm('Es-tu sûr de vouloir supprimer cet article ?')) return;

    const token = localStorage.getItem('client_token');

    try {
      const response = await fetch(`${API_URL}/api/blog/${articleId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success('Article supprimé');
        fetchArticles();
      }
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const openEditModal = (article) => {
    setEditingArticle(article);
    setNewArticle({
      title: article.title,
      slug: article.slug,
      excerpt: article.excerpt || '',
      content: article.content,
      image_url: article.image_url || '',
      og_image_url: article.og_image_url || '',
      category: article.category || 'recrutement',
      status: article.status,
      tags: article.tags || [],
      meta_title: article.meta_title || '',
      meta_description: article.meta_description || '',
      meta_keywords: article.meta_keywords || '',
      canonical_url: article.canonical_url || '',
      no_index: article.no_index || false
    });
    setActiveTab('content');
    setShowCreateModal(true);
  };

  const resetForm = () => {
    setNewArticle({
      title: '',
      slug: '',
      excerpt: '',
      content: '',
      image_url: '',
      og_image_url: '',
      category: 'recrutement',
      status: 'draft',
      tags: [],
      meta_title: '',
      meta_description: '',
      meta_keywords: '',
      canonical_url: '',
      no_index: false
    });
    setEditingArticle(null);
    setActiveTab('content');
  };

  const addTag = () => {
    if (newTag.trim() && !newArticle.tags.includes(newTag.trim())) {
      setNewArticle(prev => ({
        ...prev,
        tags: [...prev.tags, newTag.trim()]
      }));
      setNewTag('');
    }
  };

  const removeTag = (tagToRemove) => {
    setNewArticle(prev => ({
      ...prev,
      tags: prev.tags.filter(tag => tag !== tagToRemove)
    }));
  };

  const getStatusInfo = (status) => statusOptions.find(s => s.value === status) || statusOptions[0];

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  const getSeoScoreColor = (score) => {
    if (score >= 80) return 'text-green-500';
    if (score >= 50) return 'text-amber-500';
    return 'text-red-500';
  };

  const getSeoScoreBg = (score) => {
    if (score >= 80) return 'bg-green-500';
    if (score >= 50) return 'bg-amber-500';
    return 'bg-red-500';
  };

  const filteredArticles = articles.filter(article =>
    article.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    article.excerpt?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="blog-loading">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-rm-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="blog-manage-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-rm-text">Gestion du Blog</h1>
          <p className="text-rm-text-secondary">Crée et gère tes articles de blog optimisés SEO</p>
        </div>
        <Button
          onClick={() => {
            resetForm();
            setShowCreateModal(true);
          }}
          className="bg-rm-primary hover:bg-rm-primary/90"
          data-testid="create-article-btn"
        >
          <Plus className="w-5 h-5 mr-2" />
          Nouvel article
        </Button>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Rechercher un article..."
          className="w-full pl-10 pr-4 py-2 bg-white border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-rm-primary/20"
        />
      </div>

      {/* Articles List */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-100">
        {filteredArticles.length === 0 ? (
          <div className="text-center py-12">
            <FileText className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <p className="text-rm-text-secondary">Aucun article trouvé</p>
            <Button
              onClick={() => setShowCreateModal(true)}
              className="mt-4 bg-rm-primary"
            >
              <Plus className="w-4 h-4 mr-2" />
              Créer un article
            </Button>
          </div>
        ) : (
          <div className="divide-y divide-slate-100">
            {filteredArticles.map((article) => {
              const statusInfo = getStatusInfo(article.status);
              return (
                <div
                  key={article.id}
                  className="p-4 hover:bg-slate-50 transition-colors"
                  data-testid={`article-row-${article.id}`}
                >
                  <div className="flex items-start gap-4">
                    <div className="w-20 h-20 bg-slate-100 rounded-lg overflow-hidden flex-shrink-0">
                      {article.image_url ? (
                        <img src={article.image_url} alt={article.title} className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <FileText className="w-8 h-8 text-slate-300" />
                        </div>
                      )}
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-4">
                        <div>
                          <h3 className="font-medium text-rm-text">{article.title}</h3>
                          {article.excerpt && (
                            <p className="text-sm text-rm-text-secondary mt-1 line-clamp-2">{article.excerpt}</p>
                          )}
                        </div>
                        <span className={`text-xs px-2 py-1 rounded-full flex-shrink-0 ${statusInfo.color}`}>
                          {statusInfo.label}
                        </span>
                      </div>

                      <div className="flex flex-wrap items-center gap-3 mt-2 text-xs text-rm-text-secondary">
                        <span className="flex items-center gap-1">
                          <User className="w-3 h-3" />
                          {article.author}
                        </span>
                        <span className="flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          {formatDate(article.created_at)}
                        </span>
                        {article.meta_title && (
                          <span className="flex items-center gap-1 text-green-500">
                            <CheckCircle2 className="w-3 h-3" />
                            SEO
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="flex items-center gap-1">
                      <Button variant="ghost" size="sm" onClick={() => window.open(`/blog/${article.slug}`, '_blank')} title="Voir">
                        <Eye className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => openEditModal(article)} title="Modifier">
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => handleDeleteArticle(article.id)} className="text-red-500 hover:text-red-600 hover:bg-red-50" title="Supprimer">
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Create/Edit Modal */}
      <AnimatePresence>
        {showCreateModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
            onClick={() => { setShowCreateModal(false); resetForm(); }}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-2xl w-full max-w-4xl shadow-xl max-h-[90vh] overflow-hidden flex flex-col"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="p-6 border-b border-slate-100 flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-bold text-rm-text">
                    {editingArticle ? 'Modifier l\'article' : 'Nouvel article'}
                  </h2>
                  <div className="flex items-center gap-2 mt-1">
                    <span className={`text-sm font-medium ${getSeoScoreColor(seoScore.score)}`}>
                      Score SEO: {seoScore.score}%
                    </span>
                    <div className="w-24 h-2 bg-slate-100 rounded-full overflow-hidden">
                      <div className={`h-full ${getSeoScoreBg(seoScore.score)} transition-all`} style={{ width: `${seoScore.score}%` }} />
                    </div>
                  </div>
                </div>
                <button onClick={() => { setShowCreateModal(false); resetForm(); }} className="p-2 hover:bg-slate-100 rounded-lg">
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Tabs */}
              <div className="flex border-b border-slate-100">
                <button
                  onClick={() => setActiveTab('content')}
                  className={`flex-1 px-4 py-3 text-sm font-medium transition-colors ${
                    activeTab === 'content' ? 'text-rm-primary border-b-2 border-rm-primary' : 'text-slate-500 hover:text-slate-700'
                  }`}
                >
                  <FileText className="w-4 h-4 inline mr-2" />
                  Contenu
                </button>
                <button
                  onClick={() => setActiveTab('seo')}
                  className={`flex-1 px-4 py-3 text-sm font-medium transition-colors ${
                    activeTab === 'seo' ? 'text-rm-primary border-b-2 border-rm-primary' : 'text-slate-500 hover:text-slate-700'
                  }`}
                >
                  <Globe className="w-4 h-4 inline mr-2" />
                  SEO & Référencement
                </button>
                <button
                  onClick={() => setActiveTab('images')}
                  className={`flex-1 px-4 py-3 text-sm font-medium transition-colors ${
                    activeTab === 'images' ? 'text-rm-primary border-b-2 border-rm-primary' : 'text-slate-500 hover:text-slate-700'
                  }`}
                >
                  <Image className="w-4 h-4 inline mr-2" />
                  Images
                </button>
              </div>

              {/* Form Content */}
              <form onSubmit={editingArticle ? handleUpdateArticle : handleCreateArticle} className="flex-1 overflow-y-auto">
                <div className="p-6 space-y-4">
                  
                  {/* Content Tab */}
                  {activeTab === 'content' && (
                    <>
                      <div>
                        <label className="block text-sm font-medium text-rm-text mb-1">Titre de l'article *</label>
                        <input
                          type="text"
                          required
                          value={newArticle.title}
                          onChange={(e) => setNewArticle({ ...newArticle, title: e.target.value })}
                          className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-rm-primary/20"
                          placeholder="Titre accrocheur de l'article"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-rm-text mb-1">Slug (URL)</label>
                        <div className="flex items-center">
                          <span className="text-sm text-slate-400 mr-2">/blog/</span>
                          <input
                            type="text"
                            value={newArticle.slug}
                            onChange={(e) => setNewArticle({ ...newArticle, slug: e.target.value })}
                            className="flex-1 px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-rm-primary/20"
                            placeholder="mon-article (généré automatiquement si vide)"
                          />
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-rm-text mb-1">Extrait / Résumé</label>
                        <textarea
                          value={newArticle.excerpt}
                          onChange={(e) => setNewArticle({ ...newArticle, excerpt: e.target.value })}
                          className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-rm-primary/20"
                          rows={2}
                          placeholder="Résumé court qui apparaîtra dans la liste des articles..."
                        />
                        <p className="text-xs text-slate-400 mt-1">{newArticle.excerpt.length}/200 caractères recommandés</p>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-rm-text mb-1">Contenu *</label>
                        <textarea
                          required
                          value={newArticle.content}
                          onChange={(e) => setNewArticle({ ...newArticle, content: e.target.value })}
                          className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-rm-primary/20 font-mono text-sm"
                          rows={12}
                          placeholder="Contenu de l'article (Markdown supporté)..."
                        />
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-rm-text mb-1">Catégorie</label>
                          <select
                            value={newArticle.category}
                            onChange={(e) => setNewArticle({ ...newArticle, category: e.target.value })}
                            className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-rm-primary/20"
                          >
                            {categoryOptions.map(cat => (
                              <option key={cat.value} value={cat.value}>{cat.label}</option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-rm-text mb-1">Statut</label>
                          <select
                            value={newArticle.status}
                            onChange={(e) => setNewArticle({ ...newArticle, status: e.target.value })}
                            className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-rm-primary/20"
                          >
                            {statusOptions.map(status => (
                              <option key={status.value} value={status.value}>{status.label}</option>
                            ))}
                          </select>
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-rm-text mb-1">Tags</label>
                        <div className="flex gap-2 mb-2">
                          <input
                            type="text"
                            value={newTag}
                            onChange={(e) => setNewTag(e.target.value)}
                            onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addTag())}
                            className="flex-1 px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-rm-primary/20"
                            placeholder="Ajouter un tag..."
                          />
                          <Button type="button" variant="outline" onClick={addTag}>Ajouter</Button>
                        </div>
                        <div className="flex flex-wrap gap-2">
                          {newArticle.tags.map((tag, idx) => (
                            <span key={idx} className="bg-rm-primary/10 text-rm-primary px-3 py-1 rounded-full text-sm flex items-center gap-1">
                              {tag}
                              <button type="button" onClick={() => removeTag(tag)} className="hover:text-red-500">
                                <X className="w-3 h-3" />
                              </button>
                            </span>
                          ))}
                        </div>
                      </div>
                    </>
                  )}

                  {/* SEO Tab */}
                  {activeTab === 'seo' && (
                    <>
                      {/* SEO Tips */}
                      {seoScore.tips.length > 0 && (
                        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                          <h4 className="font-medium text-amber-800 mb-2 flex items-center gap-2">
                            <AlertCircle className="w-4 h-4" />
                            Conseils d'optimisation
                          </h4>
                          <ul className="space-y-1">
                            {seoScore.tips.map((tip, idx) => (
                              <li key={idx} className="text-sm text-amber-700 flex items-start gap-2">
                                <span className="text-amber-400 mt-1">•</span>
                                {tip}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}

                      <div>
                        <label className="block text-sm font-medium text-rm-text mb-1">
                          Titre SEO (balise title)
                          <span className="text-slate-400 font-normal ml-2">
                            {(newArticle.meta_title || newArticle.title).length}/60 caractères
                          </span>
                        </label>
                        <input
                          type="text"
                          value={newArticle.meta_title}
                          onChange={(e) => setNewArticle({ ...newArticle, meta_title: e.target.value })}
                          className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-rm-primary/20"
                          placeholder={newArticle.title || "Titre optimisé pour les moteurs de recherche"}
                          maxLength={70}
                        />
                        <p className="text-xs text-slate-400 mt-1">Laissez vide pour utiliser le titre de l'article. Idéal: 30-60 caractères</p>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-rm-text mb-1">
                          Méta description
                          <span className="text-slate-400 font-normal ml-2">
                            {newArticle.meta_description.length}/160 caractères
                          </span>
                        </label>
                        <textarea
                          value={newArticle.meta_description}
                          onChange={(e) => setNewArticle({ ...newArticle, meta_description: e.target.value })}
                          className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-rm-primary/20"
                          rows={3}
                          placeholder="Description qui apparaîtra dans les résultats de recherche Google..."
                          maxLength={170}
                        />
                        <p className="text-xs text-slate-400 mt-1">Idéal: 120-160 caractères. Inclus tes mots-clés principaux.</p>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-rm-text mb-1">Mots-clés SEO</label>
                        <input
                          type="text"
                          value={newArticle.meta_keywords}
                          onChange={(e) => setNewArticle({ ...newArticle, meta_keywords: e.target.value })}
                          className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-rm-primary/20"
                          placeholder="recrutement, madagascar, assistant virtuel, ..."
                        />
                        <p className="text-xs text-slate-400 mt-1">Séparez les mots-clés par des virgules (5-10 mots-clés recommandés)</p>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-rm-text mb-1">URL canonique (optionnel)</label>
                        <input
                          type="url"
                          value={newArticle.canonical_url}
                          onChange={(e) => setNewArticle({ ...newArticle, canonical_url: e.target.value })}
                          className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-rm-primary/20"
                          placeholder="https://recrutermalin.com/blog/..."
                        />
                        <p className="text-xs text-slate-400 mt-1">Utilisez si cet article est republié depuis une autre source</p>
                      </div>

                      <div className="flex items-center gap-3 p-4 bg-slate-50 rounded-lg">
                        <input
                          type="checkbox"
                          id="no_index"
                          checked={newArticle.no_index}
                          onChange={(e) => setNewArticle({ ...newArticle, no_index: e.target.checked })}
                          className="w-4 h-4 rounded border-slate-300 text-rm-primary focus:ring-rm-primary"
                        />
                        <label htmlFor="no_index" className="text-sm text-rm-text">
                          <span className="font-medium">Ne pas indexer cet article</span>
                          <span className="block text-slate-500">L'article ne sera pas référencé par Google (noindex)</span>
                        </label>
                      </div>

                      {/* Preview */}
                      <div className="border border-slate-200 rounded-lg p-4">
                        <h4 className="text-sm font-medium text-rm-text mb-3 flex items-center gap-2">
                          <Eye className="w-4 h-4" />
                          Aperçu Google
                        </h4>
                        <div className="bg-white p-3 rounded border border-slate-100">
                          <p className="text-blue-600 text-lg hover:underline cursor-pointer truncate">
                            {newArticle.meta_title || newArticle.title || 'Titre de l\'article'}
                          </p>
                          <p className="text-green-700 text-sm truncate">
                            recrutermalin.com/blog/{newArticle.slug || 'mon-article'}
                          </p>
                          <p className="text-sm text-slate-600 mt-1 line-clamp-2">
                            {newArticle.meta_description || newArticle.excerpt || 'La description de ton article apparaîtra ici...'}
                          </p>
                        </div>
                      </div>
                    </>
                  )}

                  {/* Images Tab */}
                  {activeTab === 'images' && (
                    <>
                      {/* Image de couverture */}
                      <div className="border border-slate-200 rounded-lg p-4">
                        <div className="flex items-start gap-4">
                          <div className="w-10 h-10 bg-rm-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                            <Image className="w-5 h-5 text-rm-primary" />
                          </div>
                          <div className="flex-1">
                            <h4 className="font-medium text-rm-text">Image de couverture</h4>
                            <p className="text-sm text-slate-500 mb-3">Affichée sur la page de l'article</p>
                            
                            <div className="bg-blue-50 rounded-lg p-3 mb-3">
                              <p className="text-sm text-blue-700 flex items-center gap-2">
                                <Info className="w-4 h-4" />
                                <span><strong>Taille recommandée:</strong> {IMAGE_SPECS.cover.width} x {IMAGE_SPECS.cover.height} px (ratio {IMAGE_SPECS.cover.ratio})</span>
                              </p>
                              <p className="text-xs text-blue-600 mt-1">Formats acceptés : PNG, JPEG, JPG, WebP</p>
                            </div>

                            <input
                              type="file"
                              ref={coverInputRef}
                              accept=".png,.jpg,.jpeg,.webp"
                              className="hidden"
                              onChange={async (e) => {
                                const file = e.target.files?.[0];
                                if (!file) return;
                                
                                setUploadingCover(true);
                                const token = localStorage.getItem('client_token');
                                
                                try {
                                  const formData = new FormData();
                                  formData.append('file', file);
                                  formData.append('file_type', 'document');
                                  
                                  const response = await fetch(`${API_URL}/api/files/upload`, {
                                    method: 'POST',
                                    headers: { 'Authorization': `Bearer ${token}` },
                                    body: formData
                                  });
                                  
                                  if (response.ok) {
                                    const result = await response.json();
                                    setNewArticle(prev => ({ ...prev, image_url: `${API_URL}${result.file_url}` }));
                                    toast.success('Image de couverture téléchargée');
                                  } else {
                                    toast.error("Erreur lors de l'upload");
                                  }
                                } catch (error) {
                                  toast.error("Erreur lors de l'upload");
                                } finally {
                                  setUploadingCover(false);
                                  if (coverInputRef.current) coverInputRef.current.value = '';
                                }
                              }}
                            />

                            {newArticle.image_url ? (
                              <div className="space-y-3">
                                <div className="relative group">
                                  <img 
                                    src={newArticle.image_url} 
                                    alt="Aperçu couverture" 
                                    className="w-full max-h-48 object-cover rounded-lg border border-slate-200"
                                    onError={(e) => e.target.style.display = 'none'}
                                  />
                                  <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex items-center justify-center gap-2">
                                    <Button
                                      type="button"
                                      size="sm"
                                      variant="secondary"
                                      onClick={() => coverInputRef.current?.click()}
                                      disabled={uploadingCover}
                                    >
                                      Changer
                                    </Button>
                                    <Button
                                      type="button"
                                      size="sm"
                                      variant="destructive"
                                      onClick={() => setNewArticle(prev => ({ ...prev, image_url: '' }))}
                                    >
                                      Supprimer
                                    </Button>
                                  </div>
                                </div>
                              </div>
                            ) : (
                              <button
                                type="button"
                                onClick={() => coverInputRef.current?.click()}
                                disabled={uploadingCover}
                                className="w-full border-2 border-dashed border-slate-200 rounded-lg p-6 hover:border-rm-primary hover:bg-rm-primary/5 transition-colors disabled:opacity-50"
                              >
                                {uploadingCover ? (
                                  <div className="flex flex-col items-center">
                                    <Loader2 className="w-8 h-8 text-rm-primary animate-spin mb-2" />
                                    <p className="text-sm text-rm-text">Upload en cours...</p>
                                  </div>
                                ) : (
                                  <div className="flex flex-col items-center">
                                    <Upload className="w-8 h-8 text-slate-400 mb-2" />
                                    <p className="text-sm font-medium text-rm-text">Cliquez pour télécharger une image</p>
                                    <p className="text-xs text-slate-500 mt-1">PNG, JPEG ou WebP (max 25 MB)</p>
                                  </div>
                                )}
                              </button>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Image de partage social */}
                      <div className="border border-slate-200 rounded-lg p-4">
                        <div className="flex items-start gap-4">
                          <div className="w-10 h-10 bg-rm-secondary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                            <Share2 className="w-5 h-5 text-rm-secondary" />
                          </div>
                          <div className="flex-1">
                            <h4 className="font-medium text-rm-text">Image de partage (Open Graph)</h4>
                            <p className="text-sm text-slate-500 mb-3">Affichée lors du partage sur Facebook, LinkedIn, Twitter...</p>
                            
                            <div className="bg-purple-50 rounded-lg p-3 mb-3">
                              <p className="text-sm text-purple-700 flex items-center gap-2">
                                <Info className="w-4 h-4" />
                                <span><strong>Taille recommandée:</strong> {IMAGE_SPECS.social.width} x {IMAGE_SPECS.social.height} px (ratio {IMAGE_SPECS.social.ratio})</span>
                              </p>
                              <p className="text-xs text-purple-600 mt-1">Format optimisé pour Facebook, LinkedIn et Twitter • PNG, JPEG, WebP</p>
                            </div>

                            <input
                              type="file"
                              ref={ogInputRef}
                              accept=".png,.jpg,.jpeg,.webp"
                              className="hidden"
                              onChange={async (e) => {
                                const file = e.target.files?.[0];
                                if (!file) return;
                                
                                setUploadingOg(true);
                                const token = localStorage.getItem('client_token');
                                
                                try {
                                  const formData = new FormData();
                                  formData.append('file', file);
                                  formData.append('file_type', 'document');
                                  
                                  const response = await fetch(`${API_URL}/api/files/upload`, {
                                    method: 'POST',
                                    headers: { 'Authorization': `Bearer ${token}` },
                                    body: formData
                                  });
                                  
                                  if (response.ok) {
                                    const result = await response.json();
                                    setNewArticle(prev => ({ ...prev, og_image_url: `${API_URL}${result.file_url}` }));
                                    toast.success('Image de partage téléchargée');
                                  } else {
                                    toast.error("Erreur lors de l'upload");
                                  }
                                } catch (error) {
                                  toast.error("Erreur lors de l'upload");
                                } finally {
                                  setUploadingOg(false);
                                  if (ogInputRef.current) ogInputRef.current.value = '';
                                }
                              }}
                            />

                            {newArticle.og_image_url ? (
                              <div className="space-y-3">
                                <div className="relative group">
                                  <img 
                                    src={newArticle.og_image_url} 
                                    alt="Aperçu partage social" 
                                    className="w-full max-h-48 object-cover rounded-lg border border-slate-200"
                                    onError={(e) => e.target.style.display = 'none'}
                                  />
                                  <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex items-center justify-center gap-2">
                                    <Button
                                      type="button"
                                      size="sm"
                                      variant="secondary"
                                      onClick={() => ogInputRef.current?.click()}
                                      disabled={uploadingOg}
                                    >
                                      Changer
                                    </Button>
                                    <Button
                                      type="button"
                                      size="sm"
                                      variant="destructive"
                                      onClick={() => setNewArticle(prev => ({ ...prev, og_image_url: '' }))}
                                    >
                                      Supprimer
                                    </Button>
                                  </div>
                                </div>
                              </div>
                            ) : (
                              <button
                                type="button"
                                onClick={() => ogInputRef.current?.click()}
                                disabled={uploadingOg}
                                className="w-full border-2 border-dashed border-slate-200 rounded-lg p-6 hover:border-rm-secondary hover:bg-rm-secondary/5 transition-colors disabled:opacity-50"
                              >
                                {uploadingOg ? (
                                  <div className="flex flex-col items-center">
                                    <Loader2 className="w-8 h-8 text-rm-secondary animate-spin mb-2" />
                                    <p className="text-sm text-rm-text">Upload en cours...</p>
                                  </div>
                                ) : (
                                  <div className="flex flex-col items-center">
                                    <Share2 className="w-8 h-8 text-slate-400 mb-2" />
                                    <p className="text-sm font-medium text-rm-text">Cliquez pour télécharger une image</p>
                                    <p className="text-xs text-slate-500 mt-1">Laissez vide pour utiliser l'image de couverture</p>
                                  </div>
                                )}
                              </button>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Conseils images */}
                      <div className="bg-slate-50 rounded-lg p-4">
                        <h4 className="font-medium text-rm-text mb-3 flex items-center gap-2">
                          <AlertCircle className="w-4 h-4 text-slate-500" />
                          Conseils pour tes images
                        </h4>
                        <ul className="space-y-2 text-sm text-slate-600">
                          <li className="flex items-start gap-2">
                            <CheckCircle2 className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                            <span>Utilisez des images de haute qualité (minimum 1200px de large)</span>
                          </li>
                          <li className="flex items-start gap-2">
                            <CheckCircle2 className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                            <span>Compresse tes images avant l'upload pour un chargement rapide</span>
                          </li>
                          <li className="flex items-start gap-2">
                            <CheckCircle2 className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                            <span>Évitez le texte sur les images de partage (peut être coupé)</span>
                          </li>
                          <li className="flex items-start gap-2">
                            <CheckCircle2 className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                            <span>Placez les éléments importants au centre de l'image</span>
                          </li>
                        </ul>
                      </div>
                    </>
                  )}
                </div>

                {/* Footer */}
                <div className="p-4 border-t border-slate-100 flex justify-between items-center bg-slate-50">
                  <div className="flex items-center gap-2">
                    <span className={`text-sm font-medium ${getSeoScoreColor(seoScore.score)}`}>
                      SEO: {seoScore.score}%
                    </span>
                    {seoScore.score >= 80 && (
                      <span className="text-xs text-green-600 bg-green-100 px-2 py-0.5 rounded-full">Excellent</span>
                    )}
                    {seoScore.score >= 50 && seoScore.score < 80 && (
                      <span className="text-xs text-amber-600 bg-amber-100 px-2 py-0.5 rounded-full">Peut être amélioré</span>
                    )}
                    {seoScore.score < 50 && (
                      <span className="text-xs text-red-600 bg-red-100 px-2 py-0.5 rounded-full">À optimiser</span>
                    )}
                  </div>
                  <div className="flex gap-3">
                    <Button type="button" variant="outline" onClick={() => { setShowCreateModal(false); resetForm(); }}>
                      Annuler
                    </Button>
                    <Button type="submit" className="bg-rm-primary">
                      <Save className="w-4 h-4 mr-2" />
                      {editingArticle ? 'Mettre à jour' : 'Créer l\'article'}
                    </Button>
                  </div>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
